import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable, of} from 'rxjs';
import {HttpService} from '../../core/services/http.service';
import {environment as env} from '../../../environments/environment';
import {
    FiltersQueryModel,
    FiltersEnum,
    FilterOutputModel,
    FilterSimulResultsAlertsEnum, CategoriesTreeFlatList, CategoriesTree,
} from '../models';
import {SettingsProvider} from './settings.service';
import {map} from 'rxjs/operators';
import {TableColumnDef} from 'app/shared/tables/table-column-def';
import {HttpParams} from '@angular/common/http';
import {ICategorySelected} from '../../components/new-table/select-by-column/select-by-column.component';
import {GeneralService} from './general.service';

@Injectable()
export class FiltersService {
    private filters = new BehaviorSubject(<FiltersQueryModel>null);

    constructor(
        private http: HttpService,
        public settingsService: SettingsProvider,
        private generalService: GeneralService
    ) {
    }

    $filters = this.filters.asObservable();

    public hasFiltersLoaded(): boolean {
        if (!this.filters.getValue()) {
            return false;
        }

        return true;
    }

    public initFilters(resFilters: FiltersQueryModel) {
        this.filters.next(resFilters);
    }

    public getFiltersByName(filter: string): Array<any> {
        if (!this.filters.getValue()) {
            return [];
        }
        return this.filters.getValue().filters[filter];
    }

    public fetchFilters(): Observable<FiltersQueryModel> {
        const url = `${this.settingsService.configuration.url}${env.FiltersDataService.apiUrlGetList}`;
        return this.http.get(url).pipe(
            map((response) => {
                return {
                    filters: {
                        ...response.filters,
                        SimulationAlerts: [
                            FilterSimulResultsAlertsEnum.Exist,
                            FilterSimulResultsAlertsEnum.Noexist,
                        ],
                        CategoryRoles: [
                            ...response.filters.ProductCategoryRoles
                        ],
                        ProductEan: [
                            ...response.filters.EANs
                        ],
                        Categories: this.generateCategoriesTree(response.filters.Categories.filter(category => {
                            return !!(category.name);
                        }))
                    }

                };
            })
        );
    }

    public setFilters(
        filter: string,
        selectFilters: Array<any>,
        filterString: string,
        selectFilterString
    ): string {
        let mainFilterString = '';
        if (filter) {
            mainFilterString = filterString;
        }
        if (filter && selectFilters && !!selectFilters.length) {
            mainFilterString = mainFilterString + ' and ';
        }
        if (selectFilters && !!selectFilters.length) {
            mainFilterString = mainFilterString.concat('', selectFilterString);
        }
        return mainFilterString;
    }

    public setSelectFilters(selectedFilters: FilterOutputModel[]): string {
        return selectedFilters
            .map((el, index) => {
                const filterString = el.filterValues
                    .map((filterVal, indx) =>
                        indx > 0
                            ? ` or ${
                                FiltersEnum[el.filterName]
                            } eq '${filterVal}'`
                            : `${FiltersEnum[el.filterName]} eq '${filterVal}'`
                    )
                    .join('');
                return index > 0
                    ? ` and (${filterString})`
                    : `(${filterString})`;
            })
            .join('');
    }

    public setSelectFiltersForList(
        selectedFilters: FilterOutputModel[],
        tableInfo: Array<TableColumnDef>
    ): string {
        if (selectedFilters === undefined || selectedFilters === null) {
            return null;
        }

        selectedFilters.forEach((selectedFilter) => {
            if (typeof selectedFilter.filterValues[0] === 'object') {
                selectedFilter.filterValues = selectedFilter.filterValues[0];
            }
        });

        const filterQueryStringNew = selectedFilters
            .map((el: FilterOutputModel) => {
                let columnDef = el.columnDef;
                if (columnDef === undefined) {
                    columnDef = tableInfo.find(
                        (t: TableColumnDef) => t.filterValue === el.filterName
                    );
                }

                let fieldsFilter;

                if (el.columnDef.filterValue === 'Categories') {
                    const propBaseName = el.columnDef.filterNameProp || 'CategoryIdLevel';
                    const parentSelected: number[] = el.filterValues.filter((category) => !category.leaf).map((parent) => parent.id);
                    //
                    // const parentLength: {[parentId: number]: number} = this.getParentChildrenLength(parentSelected);
                    // const skipParent = [];
                    //
                    // parentSelected.forEach((parentId) => {
                    //     if (parentLength && parentLength[parentId]) {
                    //         const childLength = el.filterValues.filter((category) => category.parentId === parentId).length;
                    //         if (childLength < parentLength[parentId]) {
                    //             skipParent.push(parentId);
                    //         }
                    //     }
                    // });

                    const idsByLevel: {[level: number]: number[]} = {};

                    fieldsFilter = el.filterValues
                        .filter((e: ICategorySelected) => !parentSelected.includes(e.parentId))
                        .forEach((e: ICategorySelected) => {
                            if (!idsByLevel[e.level]) {
                                idsByLevel[e.level] = [];
                            }

                            idsByLevel[e.level].push(e.id);
                        });

                    const levelKeys = Object.keys(idsByLevel);

                    fieldsFilter = levelKeys.map((level: string) => {
                        return '(' + propBaseName + level + ` in (${idsByLevel[level].join(',')}))`;

                    }).join(' or ');
                } else {
                    fieldsFilter = el.filterValues
                        .map((e) => {
                            let fieldS = '';
                            let columnName = '';
                            if (
                                columnDef.filterColumnDef !== undefined &&
                                columnDef.filterColumnDef !== null
                            ) {
                                columnName = columnDef.filterColumnDef;
                            } else {
                                columnName = columnDef.columnDef;
                            }

                            if (columnDef.filterValue && FiltersEnum[columnDef.filterValue]) {
                                columnName = FiltersEnum[columnDef.filterValue];
                            }

                            if (columnDef.filterNameProp) {
                                columnName = columnDef.filterNameProp;
                            }

                            if (columnName === 'alerts' || columnName === 'Alerts') {
                                if (e === FilterSimulResultsAlertsEnum.Exist) {
                                    fieldS = `${columnName} eq true`;
                                } else {
                                    fieldS = `${columnName} eq false`;
                                }

                                return fieldS;
                            }

                            if (columnDef.type === 'number') {
                                fieldS = `${columnName} eq ${e}`;
                            } else if (columnDef.searchByEq) {
                                fieldS = `${columnName} eq '${e}'`;
                            } else {
                                fieldS = `contains(${columnName}, '${e}')`;
                            }

                            return fieldS;
                        })
                        .join(' or ');
                }

                return `(${fieldsFilter})`;
            })
            .join(' and ');
        return filterQueryStringNew;
    }

    public getFiltersByNameObservable(filterName: string): Observable<any> {
        return this.fetchFiltersByName(filterName);
    }

    public fetchFiltersByName(name?: string): Observable<FiltersQueryModel> {
        const url = `${this.settingsService.configuration.url}${env.FiltersDataService.apiUrlGetList}/${name}`;
        return this.http.get(url).pipe(
            map((response) => {
                // add simulation alerts
                response.filters.SimulationAlerts = [
                    FilterSimulResultsAlertsEnum.Exist,
                    FilterSimulResultsAlertsEnum.Noexist,
                ];
                return response;
            })
        );
    }

    private generateCategoriesTree(categories: CategoriesTreeFlatList[]) {
        const categoriesMap = {};
        const roots: CategoriesTree[] = [];

        const categoriesReady: CategoriesTree[] = categories.filter(category => {
            return !!(category.name);
        }).map((category, index) => {
            categoriesMap[category.id] = index;

            return {
                id: category.id,
                label: category.name,
                children: [],
                parentId: category.parentId,
                leaf: true,
                level: (!category.parentId) ? 1 : null
            };
        });

        // this.generalService.treeMaxLevel = 1;

        for (let i = 0, len = categoriesReady.length; i < len; i += 1) {
            const node = categoriesReady[i];
            if (node.parentId) {
                if (categoriesReady[categoriesMap[node.parentId]]) {
                    const parentTmp = categoriesReady[categoriesMap[node.parentId]];
                    node.level = parentTmp.level + 1;
                    parentTmp.children.push(node);
                    parentTmp.leaf = false;

                    // if (node.level > this.generalService.treeMaxLevel) {
                    //     this.generalService.treeMaxLevel = node.level;
                    // }
                } else {
                    // console.log('Not found', node);
                }

            } else {
                roots.push(node);
            }
        }

        return roots;
    }

    private getParentChildrenLength(parentIds: number[]) {
        const orgFilterData: CategoriesTree[] = this.getFiltersByName('Categories');
        const parentChildrenLength: {[parentId: number]: number} = {};

        parentIds.forEach((parentId) => {
            parentChildrenLength[parentId] = this.findChildrenLength(parentId, orgFilterData);
        });

        return parentChildrenLength;
    }

    private findChildrenLength(parentId: number, data: CategoriesTree[]) {
        const foundParent = data.find((treeNode) => treeNode.id === parentId);

        if (foundParent) {
            return foundParent.children.length;
        } else {
            let childrenLength = -1;

            for (let i = 0, len = data.length; i < len; i++) {
                const treeNode = data[i];

                if (treeNode.children && treeNode.children.length > 0) {
                    if (!treeNode.children[0].leaf) {
                        const res = this.findChildrenLength(parentId, treeNode.children);
                        if (res) {
                            childrenLength = res;
                            break;
                        }
                    }
                }
            }

            return childrenLength;
        }
    }
}
